import JSZip from 'jszip';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { buildLegacyFileUrl } from '../../../services/fileUrlService';

const safeZipName = (name, fallback, used) => {
  const base = String(name || fallback || 'file')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80) || fallback || 'file';
  let candidate = base;
  let n = 2;
  while (used.has(candidate.toLowerCase())) {
    const dot = base.lastIndexOf('.');
    candidate = dot > 0
      ? `${base.slice(0, dot)}-${n}${base.slice(dot)}`
      : `${base}-${n}`;
    n += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
};

const htmlToText = (html) => {
  const raw = String(html || '');
  if (!raw.trim()) return '';
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = raw.replace(/<br\s*\/?>/gi, '\n');
    return (tmp.textContent || tmp.innerText || '').trim();
  }
  return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const fetchVaultBlob = async (fileUrl, fileName) => {
  const token = localStorage.getItem('token');
  const url = buildLegacyFileUrl(fileUrl, { originalName: fileName, download: true });
  if (!url) throw new Error(`Could not read ${fileName || 'vault file'}`);
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return response.data;
};

const toZipFile = (blob, fileName) => {
  const type = 'application/zip';
  try {
    return new File([blob], fileName, { type });
  } catch {
    return blob;
  }
};

export const vaultItemId = (item) => String(item?.id || item?._id || '');

export const buildVaultZipContents = (items) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return list.map((item) => {
    const title = String(item?.title || 'document').trim() || 'document';
    const files = [...new Set(
      (item.attachments || [])
        .filter((att) => att?.fileUrl)
        .map((att) => String(att.fileName || 'file').trim())
        .filter(Boolean)
    )];
    return files.length ? `${title} — ${files.join(', ')}` : title;
  }).filter(Boolean);
};

export const buildVaultZipFile = async (items) => {
  const zip = new JSZip();
  const used = new Set();
  const list = Array.isArray(items) ? items : [];
  let added = 0;

  for (const item of list) {
    const title = String(item?.title || 'document').trim() || 'document';
    const folder = safeZipName(title, 'document', used).replace(/\.[^.]+$/, '') || 'document';
    const files = (item.attachments || []).filter((att) => att?.fileUrl);
    const chatText = htmlToText(item.content);

    if (chatText) {
      zip.file(`${folder}/chat.txt`, chatText);
      added += 1;
    }
    for (const att of files) {
      try {
        const blob = await fetchVaultBlob(att.fileUrl, att.fileName);
        const name = safeZipName(att.fileName || 'file', 'file', new Set());
        zip.file(`${folder}/${name}`, blob);
        added += 1;
      } catch (error) {
        const note = `Could not include ${att.fileName || 'file'}: ${error?.message || 'download failed'}\n`;
        zip.file(`${folder}/MISSING.txt`, note);
        added += 1;
      }
    }
    if (!chatText && files.length === 0) {
      zip.file(`${folder}/empty.txt`, title);
      added += 1;
    }
  }

  if (added === 0) {
    throw new Error('Nothing to zip from the selected vault items');
  }

  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/zip' });
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `vault-${stamp}.zip`;
  const file = toZipFile(blob, fileName);
  if (file && typeof file === 'object' && !file.name) {
    try {
      Object.defineProperty(file, 'name', { value: fileName, configurable: true });
    } catch {
      // ignore
    }
  }
  return file;
};
